import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, 
  Gift, 
  Share2, 
  Copy, 
  Mail, 
  CheckCircle, 
  Clock, 
  Trophy,
  Star,
  Loader2,
  Send,
  Sparkles,
  Crown,
  ArrowRight,
  QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReferralProgram = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, referralsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API_URL}/api/referral/stats`, { headers }),
        axios.get(`${API_URL}/api/referral/my-referrals`, { headers }),
        axios.get(`${API_URL}/api/referral/leaderboard`).catch(() => ({ data: { leaderboard: [] } }))
      ]);
      
      setStats(statsRes.data);
      setReferrals(referralsRes.data.referrals || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      // If no code yet, create one
      if (error.response?.status === 404) {
        try {
          const codeRes = await axios.get(`${API_URL}/api/referral/my-code`, { headers });
          setStats(codeRes.data);
        } catch (e) {
          console.error('Failed to create referral code:', e);
        }
      }
    }
    setLoading(false);
  };

  const copyToClipboard = async () => {
    if (!stats?.share_url) return;
    
    try {
      await navigator.clipboard.writeText(stats.share_url);
      setCopied(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  const shareNative = async () => {
    if (!stats?.share_url) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Rejoignez drbooking.pro',
          text: `Je vous invite sur drbooking.pro ! Utilisez mon code ${stats.code} pour obtenir des avantages.`,
          url: stats.share_url
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };

  const sendInvitation = async () => {
    if (!inviteEmail) {
      toast.error('Email requis');
      return;
    }
    
    setInviting(true);
    try {
      await axios.post(
        `${API_URL}/api/referral/invite`,
        { email: inviteEmail, message: inviteMessage },
        { headers }
      );
      
      toast.success(`Invitation envoyée à ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setInviting(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Validé</Badge>;
      case 'registered':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Inscrit</Badge>;
      case 'invited':
        return <Badge className="bg-yellow-100 text-yellow-700"><Mail className="w-3 h-3 mr-1" />Invité</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="referral-program">
      {/* Hero Section */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 p-8 text-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-8 h-8" />
                <h1 className="text-2xl font-bold">Programme de Parrainage</h1>
              </div>
              <p className="text-purple-100 max-w-md">
                Invitez vos proches et gagnez des points fidélité ! 
                Vos filleuls bénéficient aussi d'avantages exclusifs.
              </p>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/20 backdrop-blur rounded-2xl p-4 text-center">
                <Sparkles className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm font-medium">Gagnez jusqu'à</p>
                <p className="text-3xl font-bold">{stats?.config?.referrer_points || 100} pts</p>
                <p className="text-xs">par parrainage réussi</p>
              </div>
            </div>
          </div>
        </div>

        <CardContent className="p-6">
          {/* Referral Code Section */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-slate-600 mb-2">Votre code de parrainage</p>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="bg-white border-2 border-dashed border-purple-300 rounded-lg p-4 text-center">
                  <span className="text-3xl font-mono font-bold text-purple-600 tracking-wider">
                    {stats?.code || 'Chargement...'}
                  </span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={copyToClipboard}
                  variant="outline"
                  className={copied ? "bg-green-50 border-green-500 text-green-600" : ""}
                >
                  {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copié !' : 'Copier le lien'}
                </Button>
                
                <Button onClick={shareNative} className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600">
                  <Share2 className="w-4 h-4 mr-2" />
                  Partager
                </Button>
              </div>
            </div>
            
            {stats?.share_url && (
              <p className="text-xs text-slate-500 mt-3 break-all">
                {stats.share_url}
              </p>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-purple-700">{stats?.total_referrals || 0}</p>
              <p className="text-xs text-purple-600">Invitations</p>
            </div>
            
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-700">{stats?.successful_referrals || 0}</p>
              <p className="text-xs text-green-600">Validés</p>
            </div>
            
            <div className="bg-yellow-50 rounded-xl p-4 text-center">
              <Clock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-700">{stats?.pending_referrals || 0}</p>
              <p className="text-xs text-yellow-600">En attente</p>
            </div>
            
            <div className="bg-pink-50 rounded-xl p-4 text-center">
              <Star className="w-6 h-6 text-pink-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-pink-700">{stats?.points_earned || 0}</p>
              <p className="text-xs text-pink-600">Points gagnés</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 flex-wrap">
            <Button 
              onClick={() => setShowInviteModal(true)}
              className="gap-2"
            >
              <Mail className="w-4 h-4" />
              Inviter par email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Comment ça marche ?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Share2 className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold mb-1">1. Partagez</h3>
              <p className="text-sm text-slate-600">
                Envoyez votre code ou lien à vos proches
              </p>
            </div>
            
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-pink-600" />
              </div>
              <h3 className="font-semibold mb-1">2. Ils s'inscrivent</h3>
              <p className="text-sm text-slate-600">
                Vos filleuls reçoivent {stats?.config?.referee_points || 50} points + {stats?.config?.referee_premium_days || 7} jours Premium
              </p>
            </div>
            
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Gift className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-1">3. Gagnez</h3>
              <p className="text-sm text-slate-600">
                Vous gagnez {stats?.config?.referrer_points || 100} points quand ils réservent
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Referrals */}
      {referrals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Mes filleuls ({referrals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {referrals.map((ref, index) => (
                <div 
                  key={ref.id || index}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{ref.email}</p>
                    <p className="text-xs text-slate-500">
                      Invité le {new Date(ref.invited_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  {getStatusBadge(ref.status)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Top Parrains
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leaderboard.map((member, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    index === 0 ? 'bg-yellow-50 border border-yellow-200' :
                    index === 1 ? 'bg-slate-100' :
                    index === 2 ? 'bg-orange-50' : 'bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-slate-300 text-slate-700' :
                      index === 2 ? 'bg-orange-300 text-orange-800' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="font-medium">{member.display_name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{member.successful_referrals} parrainages</p>
                    <p className="text-xs text-slate-500">{member.points_earned} pts</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Inviter par email
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Email de votre ami(e)</label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="ami@email.com"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Message personnalisé (optionnel)</label>
              <Input
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                placeholder="Rejoins-moi sur drbooking !"
                className="mt-1"
              />
            </div>

            <div className="bg-purple-50 rounded-lg p-3 text-sm text-purple-800">
              <p className="font-medium mb-1">Votre ami(e) recevra :</p>
              <ul className="space-y-1 text-purple-700">
                <li>• {stats?.config?.referee_points || 50} points de fidélité</li>
                <li>• {stats?.config?.referee_premium_days || 7} jours Premium gratuits</li>
              </ul>
            </div>

            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setShowInviteModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                onClick={sendInvitation}
                disabled={inviting || !inviteEmail}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-500"
              >
                {inviting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Envoyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralProgram;
